import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  Handshake,
  Plus,
  Trash2,
  Copy,
  Check,
  Ban,
  Pencil,
  Send,
  KeyRound,
} from 'lucide-react'
import { Breadcrumb } from '../../components/Breadcrumb'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { TextField, FieldLabel } from '../../components/ui/Field'
import { Select } from '../../components/ui/Select'
import { DataTable } from '../../components/table/DataTable'
import { RowActionsMenu } from '../../components/table/RowActionsMenu'
import { ExternalRevokeDialog } from '../../components/ExternalRevokeDialog'
import { RolePill } from '../../components/ui/Pills'
import { api, extractApiError, isPlanLimitError } from '../../lib/api'
import {
  departmentList,
  externalCollaboratorListResponse,
  inviteExternalCollaboratorResponse,
  workRoleList,
} from '../../lib/types'
import { useMyWorkspaces } from '../../lib/useMyWorkspaces'
import { companyNameSchema, emailSchema, nameSchema } from '../../lib/validators'
import { grantSummaryLabel } from '../../lib/externalGrants'
import { copyText } from '../../lib/clipboard'

// All known relationship kinds → full-form, title-case labels (British
// English). Kept COMPLETE (incl. legacy contractor / agency / partner) so
// existing collaborators still render their relationship in the list, even
// though those are no longer offered for NEW invites.
const KIND_LABEL = {
  freelancer: 'Freelancer',
  contractor: 'Contractor',
  client: 'Client',
  vendor: 'Vendor',
  agency: 'Agency',
  partner: 'Partner',
}
// Relationship options offered in the invite dropdown — the supported subset.
const KIND_OPTIONS = ['freelancer', 'client', 'vendor'].map((value) => ({
  value,
  label: KIND_LABEL[value],
}))

const STATUS_LABEL = {
  active: 'Active',
  invited: 'Invited',
  expired: 'Expired',
  revoked: 'Revoked',
}
// Solid status pills — same recipe as the Members table (coloured background +
// white text; dark mode uses a neutral chip with tinted text).
const STATUS_STYLE = {
  active:
    'bg-[color:var(--color-success)] text-white border-transparent dark:bg-white/[0.06] dark:text-emerald-300 dark:border-white/10',
  invited:
    'bg-amber-500 text-white border-transparent dark:bg-white/[0.06] dark:text-amber-300 dark:border-white/10',
  expired:
    'bg-[color:var(--color-ink-subtle)] text-white border-transparent dark:bg-white/[0.06] dark:text-[color:var(--color-ink-muted)] dark:border-white/10',
  revoked:
    'bg-[color:var(--color-priority-urgent)] text-white border-transparent dark:bg-white/[0.06] dark:text-rose-300 dark:border-white/10',
}

function StatusPill({ status }) {
  const style = STATUS_STYLE[status] ?? STATUS_STYLE.expired
  return (
    <span
      className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${style}`}
    >
      {STATUS_LABEL[status] ?? status}
    </span>
  )
}

/** Read-only credential field with a Copy button — used by the password-reset
 *  result dialog to hand the admin the new sign-in details (mirrors the Members
 *  page's reset flow). */
function CredentialRow({ label, value }) {
  // Keyed to the value so the label stays on "Copied" for the life of the
  // dialog without mislabelling a different value later.
  const [copiedValue, setCopiedValue] = useState(null)
  const done = copiedValue !== null && copiedValue === value
  async function copy() {
    // Routed through the shared helper so a refused/unavailable Clipboard API
    // falls back to a gesture-based execCommand copy, and the label stays on
    // "Copied" rather than reverting (product decision — a confirmation that
    // erases itself left users unsure the copy had happened). See lib/clipboard.js.
    if (!(await copyText(value))) {
      toast.error('Could not copy. Please copy it manually.')
      return
    }
    setCopiedValue(value)
    toast.success(`${label} copied to clipboard.`)
  }
  return (
    <div>
      <FieldLabel label={label} />
      <div className="flex items-stretch gap-2">
        <input
          readOnly
          value={value}
          onFocus={(e) => e.target.select()}
          className="w-full h-10 px-3 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-sm text-[color:var(--color-ink)] outline-none"
        />
        <Button variant="secondary" onClick={copy}>
          {done ? <Check size={14} /> : <Copy size={14} />}
          {done ? 'Copied' : 'Copy'}
        </Button>
      </div>
    </div>
  )
}

/** External Role → RolePill label + tone, mirroring the Members table:
 *  a plain User is violet, a Sub Admin (tenant admin) is amber. */
function roleLabel(r) {
  return r === 'sub_admin' ? 'Sub Admin' : 'User'
}
function roleBadge(r) {
  return { label: roleLabel(r), tone: r === 'sub_admin' ? 'amber' : 'violet' }
}

/**
 * What a collaborator may DO inside the resources they can reach. Mirrors
 * `Capability.accessLevel` and the server's PERMS matrix — the SAME levels an
 * internal member has. A collaborator granted 'full' resolves through the same
 * Space permission matrix as an internal 'full' member (delete lists/folders/
 * documents, manage members where the owner/company-wide overlay applies).
 * Reach stays scoped to the grant regardless of level.
 */
const ACCESS_LEVELS = [
  { value: 'view', label: 'View', hint: 'Read-only. Cannot comment or change anything.' },
  { value: 'comment', label: 'Comment', hint: 'Read and comment on tasks.' },
  { value: 'edit', label: 'Edit', hint: 'Create and edit tasks, lists, folders and documents.' },
  { value: 'full', label: 'Full', hint: 'Everything Edit allows, plus delete lists, folders and documents inside the granted resources.' },
]
const accessLevelLabel = (v) =>
  ACCESS_LEVELS.find((o) => o.value === v)?.label ??
  (v === 'mixed' ? 'Mixed' : null)

const EMPTY_FORM = {
  fullName: '',
  emailId: '',
  orgName: '',
  kind: '',
  // External collaborators are always 'user' — never an admin. Defaulted so the
  // Role field shows "User" selected and can't be anything else.
  role: 'user',
  workRoleId: '',
  // Defaults to Edit because that is what an external EFFECTIVELY had before
  // the level was enforced — every active Work Role meant edit. Defaulting to
  // View instead would quietly make every newly invited collaborator read-only,
  // which no admin asked for; lowering it is now a deliberate choice.
  accessLevel: 'edit',
}

export function ExternalCollaboratorsPage() {
  const qc = useQueryClient()
  const [inviteOpen, setInviteOpen] = useState(false)
  const [form, setForm] = useState(EMPTY_FORM)
  const [departmentId, setDepartmentId] = useState('')
  const [fieldErrors, setFieldErrors] = useState({})
  const [revokeTarget, setRevokeTarget] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)
  // Row pending a password reset (confirm dialog) + the freshly issued
  // credentials to show the admin once (mirrors the Members page).
  const [resetTarget, setResetTarget] = useState(null)
  const [issuedPassword, setIssuedPassword] = useState(null)
  // Non-null when the dialog is editing an existing collaborator (vs inviting).
  const [editTarget, setEditTarget] = useState(null)
  const isEdit = Boolean(editTarget)
  const [joinLink, setJoinLink] = useState(null)
  const [copied, setCopied] = useState(false)

  const { workspaces, hasMultiple } = useMyWorkspaces()

  const listQuery = useQuery({
    queryKey: ['external-collaborators'],
    queryFn: async () => {
      const res = await api.get('/external-collaborators')
      return externalCollaboratorListResponse.parse(res.data)
    },
  })
  const rows = listQuery.data?.data ?? []

  const workRolesQuery = useQuery({
    queryKey: ['work-roles', 'list'],
    enabled: inviteOpen,
    queryFn: async () => {
      const res = await api.get('/work-roles')
      return workRoleList.parse(res.data)
    },
  })
  const activeWorkRoles = useMemo(
    () => (workRolesQuery.data?.data ?? []).filter((r) => r.status === 'active'),
    [workRolesQuery.data],
  )

  // Every active Department the admin can reach, across all their Workspaces —
  // so the single Department picker works whether the tenant has one Workspace
  // or several (the picker groups by Workspace when there's more than one).
  const workspaceIdsKey = workspaces.map((w) => w.id).sort().join(',')
  const departmentsQuery = useQuery({
    queryKey: ['departments', 'invite-picker', workspaceIdsKey],
    enabled: inviteOpen && workspaces.length > 0,
    queryFn: async () => {
      const perWorkspace = await Promise.all(
        workspaces.map(async (w) => {
          const res = await api.get(`/workspaces/${w.id}/departments`)
          return departmentList
            .parse(res.data)
            .data.filter((d) => d.status === 'active')
            .map((d) => ({
              id: d.id,
              name: d.name,
              workspaceId: w.id,
              workspaceName: w.name,
            }))
        }),
      )
      return perWorkspace.flat()
    },
  })
  const departments = departmentsQuery.data ?? []

  function openInvite() {
    setEditTarget(null)
    setForm(EMPTY_FORM)
    setDepartmentId('')
    setFieldErrors({})
    setInviteOpen(true)
  }
  function openEdit(row) {
    setEditTarget(row)
    setForm({
      fullName: row.fullName ?? '',
      emailId: row.emailId ?? '',
      orgName: row.orgName ?? '',
      kind: row.kind ?? 'freelancer',
      // Externals are always 'user' now — a legacy Sub Admin row edits as User.
      role: 'user',
      workRoleId: row.workRoleId ?? '',
      // '' when the row reports 'mixed' (or has no grants): the server sent
      // several levels, so pre-selecting one would misrepresent them. The field
      // is then optional and simply left alone unless the admin picks a level.
      accessLevel: ACCESS_LEVELS.some((o) => o.value === row.accessLevel)
        ? row.accessLevel
        : '',
    })
    setDepartmentId(row.grantDepartmentIds?.[0] ?? '')
    setFieldErrors({})
    setInviteOpen(true)
  }
  function closeInvite() {
    setInviteOpen(false)
    setEditTarget(null)
  }

  function setField(name, value) {
    setForm((f) => ({ ...f, [name]: value }))
  }

  const inviteMutation = useMutation({
    mutationFn: async (payload) => {
      const res = await api.post('/external-collaborators', payload)
      return inviteExternalCollaboratorResponse.parse(res.data)
    },
    onSuccess: (result) => {
      setInviteOpen(false)
      setCopied(false)
      setJoinLink(result.joinLink)
      void qc.invalidateQueries({ queryKey: ['external-collaborators'] })
      toast.success('External collaborator invited successfully.')
    },
    onError: (err) => {
      if (isPlanLimitError(err)) return
      const { message, fields } = extractApiError(err)
      if (fields) setFieldErrors((prev) => ({ ...prev, ...fields }))
      toast.error(message)
    },
  })

  const updateMutation = useMutation({
    mutationFn: async ({ seatId, payload }) => {
      await api.patch(`/external-collaborators/${seatId}`, payload)
    },
    onSuccess: () => {
      setInviteOpen(false)
      setEditTarget(null)
      void qc.invalidateQueries({ queryKey: ['external-collaborators'] })
      toast.success('External collaborator updated successfully.')
    },
    onError: (err) => {
      if (isPlanLimitError(err)) return
      const { message, fields } = extractApiError(err)
      if (fields) setFieldErrors((prev) => ({ ...prev, ...fields }))
      toast.error(message)
    },
  })

  // Revoke-with-handover lives in <ExternalRevokeDialog>: it previews the
  // collaborator's workload, requires a destination colleague when there is work
  // to move, reassigns it, and only then calls the revoke endpoint.

  // Re-send the join link to a still-pending collaborator (mints a fresh
  // token; the old link stops working). Accepting the email invite is the ONLY
  // way to activate an external collaborator, so this is how a Company Admin
  // recovers an expired or lost invitation.
  const resendMutation = useMutation({
    mutationFn: async (seatId) => {
      await api.post(`/external-collaborators/${seatId}/resend`)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['external-collaborators'] })
      toast.success('Invitation resent. A fresh acceptance link is on its way.')
    },
    onError: (err) => {
      if (isPlanLimitError(err)) return
      toast.error(extractApiError(err).message)
    },
  })

  // Reset password — an accepted collaborator signs in with their own global
  // credential (set on /join), so an admin reset works exactly like a member
  // reset. Reuses the shared `/users/:id/reset-password` endpoint (same
  // members.manage gate this page already requires) against the seat's linked
  // User; the new temporary password is shown to the admin once.
  const resetPasswordMutation = useMutation({
    mutationFn: async (userId) => {
      const res = await api.post(`/users/${userId}/reset-password`)
      return res.data
    },
    onSuccess: (result) => {
      setResetTarget(null)
      setIssuedPassword({
        emailId: result.user.emailId,
        temporaryPassword: result.temporaryPassword,
      })
      toast.success('Password reset successfully.')
    },
    onError: (err) => {
      if (isPlanLimitError(err)) return
      toast.error(extractApiError(err).message)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (seatId) => {
      await api.delete(`/external-collaborators/${seatId}`)
    },
    onSuccess: () => {
      setDeleteTarget(null)
      void qc.invalidateQueries({ queryKey: ['external-collaborators'] })
      toast.success('External collaborator deleted successfully.')
    },
    onError: (err) => {
      if (isPlanLimitError(err)) return
      toast.error(extractApiError(err).message)
    },
  })

  function validate() {
    const errs = {}
    // Full Name — required + the same rules as Sign-up (nameSchema): letters
    // and single spaces, 2–50 characters.
    const nameResult = nameSchema('Full Name').safeParse(form.fullName)
    if (!nameResult.success) {
      errs.fullName = nameResult.error.issues[0].message
    }
    // Email ID — the shared strict validator (matches Members / Sign-up):
    // required, no spaces, no consecutive dots, valid shape.
    const emailResult = emailSchema().safeParse(form.emailId)
    if (!emailResult.success) {
      errs.email = emailResult.error.issues[0].message
    }
    // Company Name — same rules as Sign-up (companyNameSchema): letters,
    // numbers and single spaces, 2–50 characters.
    const orgResult = companyNameSchema('Company Name').safeParse(form.orgName)
    if (!orgResult.success) {
      errs.orgName = orgResult.error.issues[0].message
    }
    if (!form.kind) errs.kind = 'Please select a Relationship.'
    if (!form.role) errs.role = 'Please select a Role.'
    // Work Role + Department apply only to a 'user' collaborator. A Sub Admin
    // is company-wide (no Work Role, no department scope), so skip both.
    if (form.role === 'user') {
      if (!form.workRoleId) errs.workRoleId = 'Please select a Work Role.'
      if (!departmentId) errs.department = 'Please select a Department.'
    }
    return errs
  }

  function onSubmit(e) {
    e.preventDefault()
    const errs = validate()
    // Email is the collaborator's immutable identity — never block on it (nor
    // send it) when editing.
    if (isEdit) delete errs.email
    setFieldErrors(errs)
    if (Object.keys(errs).length > 0) return
    const isSubAdmin = form.role === 'sub_admin'

    if (isEdit) {
      updateMutation.mutate({
        seatId: editTarget.seatId,
        payload: {
          fullName: nameSchema('Full Name').parse(form.fullName),
          orgName: companyNameSchema('Company Name').parse(form.orgName),
          kind: form.kind,
          // Work Role + department apply only to a 'user' collaborator; a Sub
          // Admin is company-wide, so leave those untouched on edit.
          ...(isSubAdmin
            ? {}
            : {
                workRoleId: form.workRoleId || null,
                departmentId,
                // Omitted when the row was 'mixed' and the admin didn't choose —
                // the server then leaves every grant's level untouched.
                ...(form.accessLevel ? { accessLevel: form.accessLevel } : {}),
              }),
        },
      })
      return
    }

    inviteMutation.mutate({
      email: emailSchema().parse(form.emailId),
      orgName: companyNameSchema('Company Name').parse(form.orgName),
      kind: form.kind,
      fullName: nameSchema('Full Name').parse(form.fullName),
      role: form.role,
      // A Sub Admin is company-wide — no Work Role, no department grant.
      ...(isSubAdmin
        ? {}
        : {
            workRoleId: form.workRoleId,
            grants: [
              {
                resourceType: 'department',
                resourceId: departmentId,
                // Sent explicitly so the server's 'view' default never decides
                // this silently.
                accessLevel: form.accessLevel || 'edit',
              },
            ],
          }),
    })
  }

  async function copyJoinLink() {
    if (!joinLink) return
    // Routed through the shared helper so a refused/unavailable Clipboard API
    // falls back to a gesture-based execCommand copy, and the label stays on
    // "Copied" rather than reverting (product decision — a confirmation that
    // erases itself left users unsure the copy had happened). See lib/clipboard.js.
    if (!(await copyText(joinLink))) {
      toast.error('Could not copy the link. Please copy it manually.')
      return
    }
    setCopied(true)
    toast.success('Invitation link copied to clipboard.')
  }

  const canRevoke = (status) => status === 'active' || status === 'invited'

  const columns = useMemo(
    () => [
      {
        key: 'fullName',
        header: 'Full Name',
        grow: true,
        pinnable: true,
        minWidth: 180,
        sortAccessor: (r) => (r.fullName ?? '').toLowerCase(),
        searchAccessor: (r) => `${r.fullName ?? ''} ${r.emailId ?? ''}`,
        exportValue: (r) => r.fullName ?? '',
        cellClassName:
          'whitespace-nowrap font-medium text-[color:var(--color-ink-strong)]',
        render: (r) => r.fullName || '—',
      },
      {
        key: 'emailId',
        header: 'Email ID',
        minWidth: 200,
        sortAccessor: (r) => (r.emailId ?? '').toLowerCase(),
        searchAccessor: (r) => r.emailId ?? '',
        exportValue: (r) => r.emailId ?? '',
        cellClassName: 'whitespace-nowrap',
        render: (r) => r.emailId || '—',
      },
      {
        key: 'orgName',
        header: 'Company',
        minWidth: 150,
        sortAccessor: (r) => (r.orgName ?? '').toLowerCase(),
        exportValue: (r) => r.orgName ?? '',
        cellClassName: 'whitespace-nowrap text-[color:var(--color-ink)]',
        render: (r) => r.orgName || '—',
      },
      {
        key: 'role',
        header: 'Role',
        minWidth: 110,
        sortAccessor: (r) => roleLabel(r.role),
        exportValue: (r) => roleLabel(r.role),
        render: (r) => <RolePill {...roleBadge(r.role)} />,
      },
      {
        key: 'workRoleName',
        header: 'Work Role',
        minWidth: 130,
        sortAccessor: (r) => (r.workRoleName ?? '').toLowerCase(),
        exportValue: (r) => r.workRoleName ?? '',
        cellClassName: 'whitespace-nowrap',
        render: (r) => r.workRoleName || '—',
      },
      {
        key: 'department',
        header: 'Department',
        minWidth: 150,
        sortAccessor: (r) => grantSummaryLabel(r).toLowerCase(),
        exportValue: (r) => grantSummaryLabel(r),
        cellClassName: 'whitespace-nowrap',
        render: (r) => grantSummaryLabel(r),
      },
      {
        key: 'accessLevel',
        header: 'Access',
        minWidth: 110,
        sortAccessor: (r) => accessLevelLabel(r.accessLevel) ?? '',
        exportValue: (r) => accessLevelLabel(r.accessLevel) ?? '',
        cellClassName: 'whitespace-nowrap',
        render: (r) => accessLevelLabel(r.accessLevel) ?? '—',
      },
      {
        key: 'status',
        header: 'Status',
        minWidth: 110,
        sortAccessor: (r) => r.status ?? '',
        exportValue: (r) => STATUS_LABEL[r.status] ?? r.status ?? '',
        render: (r) => <StatusPill status={r.status} />,
      },
      {
        key: 'actions',
        header: 'Actions',
        pinnedRight: true,
        align: 'center',
        minWidth: 72,
        sortable: false,
        exportable: false,
        hideable: false,
        render: (r) => (
          <RowActionsMenu
            label={`Actions for ${r.fullName || r.emailId}`}
            items={[
              r.status === 'active' &&
                r.userId && {
                  key: 'reset',
                  label: 'Reset Password',
                  icon: KeyRound,
                  onClick: () => setResetTarget(r),
                },
              { key: 'edit', label: 'Edit', icon: Pencil, onClick: () => openEdit(r) },
              r.status === 'invited' && {
                key: 'resend',
                label: 'Resend invitation',
                icon: Send,
                onClick: () => resendMutation.mutate(r.seatId),
                disabled: resendMutation.isPending,
              },
              canRevoke(r.status) && {
                key: 'revoke',
                label: 'Revoke access',
                icon: Ban,
                onClick: () => setRevokeTarget(r),
              },
              {
                key: 'delete',
                label: 'Delete',
                icon: Trash2,
                danger: true,
                separatorBefore: true,
                onClick: () => setDeleteTarget(r),
              },
            ]}
          />
        ),
      },
    ],
    // Handlers are stable declarations / setters; resendMutation.isPending is
    // read at click time via the closure over the latest render's mutation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [resendMutation.isPending],
  )

  return (
    <div>
      <Breadcrumb current="External Collaborators" />

      {/* Stacks below `sm` so the action button is never pushed off the right edge on a phone. */}
      <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)]">
            External Collaborators
          </h1>
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
            Invite people from outside your company and share only the
            Departments they need.
          </p>
        </div>
        <Button onClick={openInvite}>
          <Plus size={14} />
          Invite Collaborator
        </Button>
      </div>

      {/* Client-side collaborators list on the shared DataTable — search / sort /
       *  pagination / export in the browser. Reset / Edit / Resend / Revoke /
       *  Delete live in the row kebab (status-gated); invite + all dialogs
       *  stay below. */}
      <DataTable
        tableId="app-external-collaborators"
        columns={columns}
        rows={rows}
        getRowId={(r) => r.seatId}
        loading={listQuery.isLoading}
        error={
          listQuery.isError ? extractApiError(listQuery.error).message : null
        }
        onRetry={() => listQuery.refetch()}
        title="External Collaborators"
        exportBaseName="external-collaborators"
        exportTitle="External Collaborators"
        searchPlaceholder="Search by Full Name or Email ID"
        defaultSort={[{ key: 'fullName', dir: 'asc' }]}
        emptyState={{
          icon: Handshake,
          title: 'No external collaborators yet',
          body: 'Invite a freelancer, contractor, client, or agency to work inside the Departments you choose — without giving them the run of your company.',
        }}
      />

      {/* Invite dialog */}
      <Modal
        open={inviteOpen}
        onClose={closeInvite}
        title={
          isEdit ? 'Edit External Collaborator' : 'Invite External Collaborator'
        }
        description={
          isEdit
            ? 'Update this collaborator’s details, Work Role, and the Department they can access.'
            : 'They join your company as an external member — scoped to only the Departments you grant.'
        }
        size="lg"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={closeInvite}
              disabled={
                isEdit ? updateMutation.isPending : inviteMutation.isPending
              }
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="invite-external-form"
              loading={
                isEdit ? updateMutation.isPending : inviteMutation.isPending
              }
            >
              {isEdit ? 'Save Changes' : 'Send Invitation'}
            </Button>
          </>
        }
      >
        <form id="invite-external-form" onSubmit={onSubmit} noValidate className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField
              label="Full Name"
              name="fullName"
              required
              maxLength={50}
              value={form.fullName}
              onChange={(e) => {
                setField('fullName', e.target.value)
                if (fieldErrors.fullName) {
                  setFieldErrors((p) => ({ ...p, fullName: undefined }))
                }
              }}
              error={fieldErrors.fullName}
              placeholder="Enter the Full Name"
            />
            <TextField
              label="Email ID"
              name="emailId"
              required
              maxLength={320}
              value={form.emailId}
              disabled={isEdit}
              onChange={(e) => {
                setField('emailId', e.target.value)
                if (fieldErrors.email) {
                  setFieldErrors((p) => ({ ...p, email: undefined }))
                }
              }}
              error={fieldErrors.email}
              placeholder="Enter the Email ID"
              helperText={isEdit ? "Email can't be changed." : undefined}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField
              label="Company Name"
              name="orgName"
              required
              maxLength={200}
              value={form.orgName}
              onChange={(e) => {
                setField('orgName', e.target.value)
                if (fieldErrors.orgName) {
                  setFieldErrors((p) => ({ ...p, orgName: undefined }))
                }
              }}
              error={fieldErrors.orgName}
              placeholder="Enter a Company Name"
            />
            <div>
              <FieldLabel label="Relationship" htmlFor="ext-kind" required />
              <Select
                id="ext-kind"
                value={form.kind}
                onChange={(e) => {
                  setField('kind', e.target.value)
                  if (fieldErrors.kind) {
                    setFieldErrors((p) => ({ ...p, kind: undefined }))
                  }
                }}
              >
                <option value="" disabled>
                  Select a Relationship
                </option>
                {KIND_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
              {fieldErrors.kind ? (
                <p role="alert" className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
                  {fieldErrors.kind}
                </p>
              ) : null}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <FieldLabel label="Role" htmlFor="ext-role" required />
              <Select
                id="ext-role"
                value={form.role}
                disabled={isEdit}
                onChange={(e) => {
                  setField('role', e.target.value)
                  if (fieldErrors.role) {
                    setFieldErrors((p) => ({ ...p, role: undefined }))
                  }
                }}
              >
                <option value="user">User</option>
              </Select>
              {fieldErrors.role ? (
                <p role="alert" className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
                  {fieldErrors.role}
                </p>
              ) : null}
            </div>
            {form.role === 'user' ? (
              <div>
                <FieldLabel label="Work Role" htmlFor="ext-work-role" required />
                {activeWorkRoles.length === 0 ? (
                  <p className="text-xs text-[color:var(--color-ink-muted)]">
                    No active Work Roles yet. Create one on the Work Roles page
                    before inviting a collaborator.
                  </p>
                ) : (
                  <Select
                    id="ext-work-role"
                    value={form.workRoleId}
                    onChange={(e) => {
                      setField('workRoleId', e.target.value)
                      if (fieldErrors.workRoleId) {
                        setFieldErrors((p) => ({ ...p, workRoleId: undefined }))
                      }
                    }}
                  >
                    <option value="">Select a Work Role</option>
                    {activeWorkRoles.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name}
                      </option>
                    ))}
                  </Select>
                )}
                {fieldErrors.workRoleId ? (
                  <p role="alert" className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
                    {fieldErrors.workRoleId}
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>

          {form.role === 'user' ? (
            <div>
              <FieldLabel label="Department" htmlFor="ext-department" required />
              {departments.length === 0 ? (
                <p className="text-xs text-[color:var(--color-ink-muted)]">
                  {departmentsQuery.isLoading
                    ? 'Loading Departments…'
                    : 'No Departments available yet. Create a Department first.'}
                </p>
              ) : (
                <Select
                  id="ext-department"
                  value={departmentId}
                  onChange={(e) => {
                    setDepartmentId(e.target.value)
                    if (fieldErrors.department) {
                      setFieldErrors((p) => ({ ...p, department: undefined }))
                    }
                  }}
                >
                  <option value="">Select a Department</option>
                  {hasMultiple
                    ? workspaces.map((w) => {
                        const wsDepts = departments.filter(
                          (d) => d.workspaceId === w.id,
                        )
                        return wsDepts.length ? (
                          <optgroup key={w.id} label={w.name}>
                            {wsDepts.map((d) => (
                              <option key={d.id} value={d.id}>
                                {d.name}
                              </option>
                            ))}
                          </optgroup>
                        ) : null
                      })
                    : departments.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.name}
                        </option>
                      ))}
                </Select>
              )}
              {fieldErrors.department ? (
                <p role="alert" className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
                  {fieldErrors.department}
                </p>
              ) : (
                <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
                  Choose the department this collaborator can access. The Work
                  Role decides which actions they hold there; Access Level below
                  decides how far those actions go.
                </p>
              )}
            </div>
          ) : null}

          {/* Access Level — the level written onto the collaborator's grants.
              It is NOT per Space: an external holds no Space membership, so
              this one level applies everywhere their grant reaches. The Share
              dialog shows it read-only and points here. */}
          {form.role === 'user' ? (
            <div>
              <FieldLabel
                label="Access Level"
                htmlFor="ext-access-level"
                required={!editTarget}
              />
              <Select
                id="ext-access-level"
                value={form.accessLevel}
                onChange={(e) => setField('accessLevel', e.target.value)}
              >
                {editTarget && !form.accessLevel ? (
                  <option value="">Mixed — leave unchanged</option>
                ) : null}
                {ACCESS_LEVELS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
              <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
                {ACCESS_LEVELS.find((o) => o.value === form.accessLevel)?.hint ??
                  'This collaborator’s grants currently carry different levels. Pick one to apply it to all of them.'}
              </p>
              {/* A grant belongs to the CONNECTION, not the seat, so changing
                  the level on a connection-wide grant moves every colleague on
                  it too. The removal flow already warns about exactly this;
                  without the same warning here the change is a silent surprise. */}
              {editTarget?.levelSharedWithCount > 0 ? (
                <p className="mt-1 text-xs text-[color:var(--color-warning)]">
                  This grant is shared with {editTarget.levelSharedWithCount}{' '}
                  other collaborator
                  {editTarget.levelSharedWithCount === 1 ? '' : 's'} from{' '}
                  {editTarget.orgName || 'the same company'} — changing the level
                  changes theirs too.
                </p>
              ) : null}
            </div>
          ) : null}
        </form>
      </Modal>

      {/* Join-link result dialog — no email is sent yet, so the admin shares
          this single-use link directly. */}
      <Modal
        open={Boolean(joinLink)}
        onClose={() => setJoinLink(null)}
        title="Invitation Created"
        description="Share this single-use link with the collaborator so they can set a password and join."
        size="ml"
        footer={
          <Button onClick={() => setJoinLink(null)}>Done</Button>
        }
      >
        <div className="flex items-stretch gap-2">
          <input
            readOnly
            value={joinLink ?? ''}
            className="w-full h-10 px-3 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-sm text-[color:var(--color-ink)] outline-none"
            onFocus={(e) => e.target.select()}
          />
          <Button variant="secondary" onClick={copyJoinLink}>
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>
        <p className="mt-3 text-xs text-[color:var(--color-ink-muted)]">
          The link expires after a single use. If it is lost, revoke the seat
          and invite the collaborator again.
        </p>
      </Modal>

      <ExternalRevokeDialog
        open={Boolean(revokeTarget)}
        seatId={revokeTarget?.seatId}
        collaboratorName={
          revokeTarget?.fullName || revokeTarget?.emailId || null
        }
        onClose={() => setRevokeTarget(null)}
        onRevoked={() => setRevokeTarget(null)}
      />

      <ConfirmDialog
        open={Boolean(resetTarget)}
        onCancel={() => setResetTarget(null)}
        onConfirm={() =>
          resetTarget?.userId &&
          resetPasswordMutation.mutate(resetTarget.userId)
        }
        confirming={resetPasswordMutation.isPending}
        title="Reset Password"
        confirmLabel="Reset Password"
        message={
          resetTarget
            ? `Reset password for ${resetTarget.fullName || resetTarget.emailId || 'this collaborator'}? A new temporary password will be generated and their existing sessions will be signed out.`
            : ''
        }
      />

      {/* Password-reset result — the new temporary password, shown once. */}
      <Modal
        open={Boolean(issuedPassword)}
        onClose={() => setIssuedPassword(null)}
        title="Password Reset"
        description="Share these sign-in details with the collaborator. The password will not be shown again."
        size="ml"
        footer={<Button onClick={() => setIssuedPassword(null)}>Done</Button>}
      >
        {issuedPassword ? (
          <div className="space-y-3">
            <CredentialRow label="Email ID" value={issuedPassword.emailId} />
            <CredentialRow
              label="Password"
              value={issuedPassword.temporaryPassword}
            />
          </div>
        ) : null}
      </Modal>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.seatId)}
        confirming={deleteMutation.isPending}
        title="Delete Collaborator?"
        confirmLabel="Delete"
        message={
          deleteTarget
            ? `${deleteTarget.fullName || deleteTarget.emailId || 'This collaborator'} will be permanently removed from your company, along with their shared grants. This cannot be undone.`
            : ''
        }
      />
    </div>
  )
}
